// Integration Utilities

import type { ConnectionStatus, DetectedType } from './types';

/**
 * Get provider logo URL
 * Falls back to a placeholder if logo fails to load
 */
export function getProviderLogoUrl(provider: string, customUrl?: string): string {
  if (customUrl) return customUrl;
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
  return `${apiBaseUrl}/static/providers/${provider}.svg`;
}

/**
 * Get human-readable label for auth mode
 */
export function getAuthModeLabel(authMode: string): string {
  const labels: Record<string, string> = {
    OAUTH2: 'OAuth 2.0',
    OAUTH1: 'OAuth 1.0',
    API_KEY: 'API Key',
    BASIC: 'Username & Password',
    OAUTH2_CC: 'Client Credentials',
    JWT: 'JWT / Private Key',
    SIGNATURE: 'Signature',
    TBA: 'Token-Based Auth',
    BILL: 'Bill.com Auth',
    TWO_STEP: 'Two-Step Auth',
    NONE: 'No Auth Required',
  };
  return labels[authMode] || authMode;
}

/**
 * Get status badge color
 */
export function getStatusColor(status: ConnectionStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-500 dark:bg-emerald-600 coral:bg-primary';
    case 'expired':
      return 'bg-amber-500 dark:bg-amber-600 coral:bg-secondary-foreground/40';
    case 'revoked':
      return 'bg-destructive dark:bg-destructive/80';
    default:
      return 'bg-muted-foreground';
  }
}

/**
 * Get status label
 */
export function getStatusLabel(status: ConnectionStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'expired':
      return 'Expired';
    case 'revoked':
      return 'Revoked';
    default:
      return status;
  }
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

/**
 * Get category badge variant
 */
export function getCategoryVariant(
  category: string
): 'default' | 'secondary' | 'outline' {
  const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
    CRM: 'default',
    AI: 'default',
    Communication: 'secondary',
    Development: 'secondary',
    Productivity: 'outline',
    Support: 'outline',
    Payments: 'default',
  };
  return variants[category] || 'outline';
}

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Generate initials from provider name
 */
export function getInitials(name: string): string {
  return name
    .split(/[\s_-]+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ============================================
// Data Browser Utilities
// ============================================

/**
 * Convert snake_case/camelCase to Title Case
 * 'first_name' -> 'First Name'
 * 'firstName' -> 'First Name'
 */
export function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Detect the type of a value for smart rendering
 */
export function detectType(value: unknown): DetectedType {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';

  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value)) return 'url';
    if (/^[\w.+-]+@[\w.-]+\.\w{2,}$/i.test(value)) return 'email';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return 'datetime';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'date';
  }

  return 'string';
}

/**
 * Format datetime for display
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleString();
}

/**
 * Truncate string with ellipsis
 */
export function truncate(value: string, maxLength: number = 100): string {
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength - 3) + '...';
}

/**
 * Download content as file
 */
export function downloadFile(
  content: string,
  filename: string,
  mimeType: string
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export data to CSV
 */
export function exportToCsv(
  data: Record<string, unknown>[],
  columns: { key: string; label: string }[],
  filename: string = 'export.csv'
): void {
  if (!data.length) return;

  const header = columns.map((c) => c.label).join(',');
  const rows = data.map((row) =>
    columns
      .map((c) => {
        const value = row[c.key];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value).includes(',')
          ? `"${String(value).replace(/"/g, '""')}"`
          : String(value);
      })
      .join(',')
  );

  const csv = [header, ...rows].join('\n');
  downloadFile(csv, filename, 'text/csv');
}
